import React, { useState } from "react";
import { useNavigate } from "react-router";
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import SettingsIcon from "@mui/icons-material/Settings";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";

import { NAV_BAR_ITEMS } from "../../../routes";
import { usePermissions } from "../../../contexts/permissions";
import useAuth from "../../../hooks/useAuth";
import { useDynamicDashboardLink } from "../../../hooks/useDashboardLink";
import SelectProjectModal from "../../../components/modals/SelectProjectModal";
import NavItem from "./NavItem";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    overflow: "hidden",
  },
  list: {
    flexGrow: 1,
    overflowY: "auto",
  },
  groupTitle: {
    px: 2,
    py: 0.5,
    display: "block",
    color: "text.secondary",
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  dividerPlaceholder: {
    my: 1.5,
    mx: "auto",
    width: 20,
    height: 2,
    bgcolor: "rgba(0, 0, 0, 0.08)",
    borderRadius: 1,
  },
};

const SidebarContent = ({ open }) => {
  const navigate = useNavigate();
  const { can, settings } = usePermissions();
  const { user } = useAuth();
  const getDashboardLink = useDynamicDashboardLink();
  const [showProjectModal, setShowProjectModal] = useState(false);

  return (
    <Box sx={styles.container}>
      <List sx={styles.list}>
        {NAV_BAR_ITEMS.map(group => {
          // Filter available items for this group
          const availableItems = group.list.filter(section => {
            const isAvailable = section.customPermissionCheck
              ? section.customPermissionCheck(can, user.role, settings)
              : section.canRules
              ? section.canRules.every(rule => can(rule))
              : true;
            return isAvailable;
          });

          // Only render the group if it has available items
          if (availableItems.length === 0) return null;

          return (
            <React.Fragment key={group.title}>
              <Collapse in={open} timeout={200}>
                <Typography variant="overline" sx={styles.groupTitle}>
                  {group.title}
                </Typography>
              </Collapse>
              <Collapse in={!open} timeout={200}>
                <Box sx={styles.dividerPlaceholder} />
              </Collapse>
              {availableItems.map((section, index) => (
                <NavItem
                  key={`${group.title}-${index}`}
                  open={open}
                  title={section.title}
                  to={section.to}
                  icon={section.icon}
                  end
                  isActiveFn={(isActive) => {
                    if (isActive) return true;
                    const pathname = window.location.pathname;
                    if (
                      section.to === "/dashboard" &&
                      (/\/projects\/[a-z0-9_-]+\/assets/.test(pathname) ||
                        /\/clients\/[a-z0-9_-]+\/assets/.test(pathname))
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/dashboard" &&
                      (/\/projects\/[a-z0-9_-]+\/capture-inventory/.test(pathname) ||
                        /\/clients\/[a-z0-9_-]+\/capture-inventory/.test(pathname))
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/planners" &&
                      pathname.indexOf("/planner") === 0
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/warehouse" &&
                      pathname.indexOf("/warehouse") === 0
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/insights" &&
                      /\/projects\/[a-z0-9_-]+\/templates\/[a-z0-9_-]+/.test(pathname)
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/calendar/month" &&
                      pathname.indexOf("/calendar") === 0
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/inventory/outstanding_orders" &&
                      pathname.indexOf("/inventory") === 0 &&
                      pathname.indexOf("/inventory/reserved") !== 0
                    ) {
                      return true;
                    }
                    if (
                      section.to === "/inventory/reserved" &&
                      pathname.indexOf("/inventory/reserved") === 0
                    ) {
                      return true;
                    }
                    return false;
                  }}
                  onClick={event => {
                    if (section.to === "/dashboard") {
                      event.preventDefault();
                      navigate(getDashboardLink());
                    } else if (
                      section.to === "/shopping" &&
                      !user.company.settings.simple_checkout_for_contract_warehouse
                    ) {
                      event.preventDefault();
                      setShowProjectModal(true);
                    } else if (section.to.indexOf("/calendar") === 0) {
                      event.preventDefault();
                      const savedView = localStorage.getItem("calendarView") || "month";
                      navigate(`/calendar/${savedView}`);
                    }
                  }}
                />
              ))}
            </React.Fragment>
          );
        })}
      </List>
      <List>
        {can("role.admin") && (
          <NavItem
            open={open}
            title="Settings"
            to="/settings/clients"
            icon={SettingsIcon}
            end
          />
        )}
      </List>
      {showProjectModal && <SelectProjectModal onClose={() => setShowProjectModal(false)} />}
    </Box>
  );
};

export default SidebarContent;
